import { PdfJsLib } from '../types';

const PDF_JS_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
const PDF_JS_WORKER_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
const PDF_SCRIPT_ID = 'pdfjs-cdn-script';

let loadPromise: Promise<PdfJsLib> | null = null;

const getLoadedPdfJs = (): PdfJsLib | null => {
  if (typeof window === 'undefined') return null;
  return window.pdfjsLib ?? null;
};

export const loadPdfJs = async (): Promise<PdfJsLib> => {
  const existing = getLoadedPdfJs();
  if (existing) {
    existing.GlobalWorkerOptions.workerSrc = PDF_JS_WORKER_SRC;
    existing.GlobalWorkerOptions.verbosity = existing.VerbosityLevel.ERRORS;
    return existing;
  }

  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise<PdfJsLib>((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('PDF.js cannot load outside browser environment.'));
      return;
    }

    const existingScript = document.getElementById(PDF_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      existingScript.addEventListener('load', () => {
        const lib = getLoadedPdfJs();
        if (!lib) {
          reject(new Error('PDF.js script loaded but library is unavailable.'));
          return;
        }
        lib.GlobalWorkerOptions.workerSrc = PDF_JS_WORKER_SRC;
        lib.GlobalWorkerOptions.verbosity = lib.VerbosityLevel.ERRORS;
        resolve(lib);
      });
      existingScript.addEventListener('error', () => {
        reject(new Error('Failed to load PDF.js script.'));
      });
      return;
    }

    const script = document.createElement('script');
    script.id = PDF_SCRIPT_ID;
    script.src = PDF_JS_SRC;
    script.async = true;

    script.addEventListener('load', () => {
      const lib = getLoadedPdfJs();
      if (!lib) {
        reject(new Error('PDF.js script loaded but library is unavailable.'));
        return;
      }
      lib.GlobalWorkerOptions.workerSrc = PDF_JS_WORKER_SRC;
      lib.GlobalWorkerOptions.verbosity = lib.VerbosityLevel.ERRORS;
      resolve(lib);
    });

    script.addEventListener('error', () => {
      reject(new Error('Failed to load PDF.js script.'));
    });

    document.head.appendChild(script);
  }).catch((error) => {
    loadPromise = null;
    throw error;
  });

  return loadPromise;
};
